<template>
	<!-- Video Splash Screen untuk Capacitor Mobile -->
	<mobile-splash-screen v-if="isMobile && showSplashVideo" @splash-finished="showSplashVideo = false" />

	<!-- Notification Banner for Foreground Push Notifications -->
	<notification-banner v-if="isMobile" />

	<!-- Member Layout -->
	<member-layout v-if="isMemberRoute && !dashboard_restric.includes(currRoute) && currRoute" :key="'member-layout-' + refreshKey">
		<router-view :key="$route.path + '-' + refreshKey" />
	</member-layout>

    <!-- Extranet Layout (handled by router-view itself as it includes ExtranetLayout) -->
    <template v-else-if="isExtranetRoute || dashboard_restric.includes(currRoute)">
        <router-view :key="$route.fullPath" />
    </template>

	<!-- Admin Layout -->
	<admin-layout v-else-if="currRoute">
		<router-view :key="$route.fullPath" />
	</admin-layout>

    <o-toast-container :toasts="toasts" @remove="removeToast" />
</template>

<style lang="scss">
#app {
	font-family: Avenir, Helvetica, Arial, sans-serif;
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
	
}

#nav {
	padding: 30px;

	a {
		font-weight: bold;
		color: #2c3e50;

		&.router-link-exact-active {
			color: #42b983;
		}
	}
}
</style>

<script>
/* global qchatInitialize */
import store from '@/store'
import AdminLayout from "@/layouts/dashboards";
import MemberLayout from "@/layouts/member/MemberLayout.vue";
import OToastContainer from '@/components/OmnihControl/OToastContainer.vue'
import { useToast } from '@/composables/useToast'
import { useRouter, useRoute } from 'vue-router'
import { computed, watch, onMounted, onBeforeUnmount, ref } from '@vue/runtime-core'
import { Capacitor } from '@capacitor/core'
import { eventBus } from '@/eventBus'
import { expireSession, isSessionVerified, markSessionVerified } from '@/services/sessionManager'
import { resetSessionFlag } from '@/services/axiosInterceptor'

import MobileSplashScreen from '@/components/MobileSplashScreen.vue'

export default {
	components: {
		AdminLayout,
		MemberLayout,
		OToastContainer,
		MobileSplashScreen
	},
	setup() {
		const router = useRouter();
		const route = useRoute();
		const { toasts, removeToast } = useToast()
		const showSplashVideo = ref(true)
		const refreshKey = ref(0)

		const isMobile = Capacitor.isNativePlatform()

		const currRoute = computed(() => route.name);

		const dashboard_restric = ['CorporateLogin', 'Login', 'MemberLogin', 'BackofficeLogin', 'MemberSignUp', 'NotFound', 'ForgotPassword', 'ResetPassword', 'MarketingRedirect', 'ExtranetLogin', 'ExtranetForgotPassword', 'PointInfo', 'newsletter_unsubscribe', 'PublicPolicies'];

		const isMemberRoute = computed(() => route.path.startsWith('/client'));
		const isExtranetRoute = computed(() => route.path.startsWith('/extranet'));

		// Handler didefinisikan SEBELUM onMounted memakainya
		const handleSessionExpired = async () => {
			if (store.state.network?.isOnline === false) {
				resetSessionFlag()
				return
			}
			const target = await expireSession()
			router.replace(target)
		}

		onMounted(async () => {
			if (!isMobile) {
				showSplashVideo.value = false;
			}
			eventBus.on('session-expired', handleSessionExpired)
		});

		onBeforeUnmount(() => {
			eventBus.off('session-expired', handleSessionExpired)
		});

		watch(
			() => store.state.network?.isOnline,
			async (isOnline, wasOnline) => {
				if (isOnline && wasOnline === false) {
					console.log('[App] Network reconnected — syncing session & refreshing active page...')
					try {
						await store.dispatch('Auth/auth_check')
						if (isMemberRoute.value) {
							await store.dispatch('ClientDashboard/read').catch(() => {})
						}
					} catch (err) {
						// 401 ditangani interceptor lewat event 'session-expired'
						console.warn('[App] auth_check after reconnect failed:', err?.response?.status ?? 'no response', '— session retained')
					}
					refreshKey.value++
					eventBus.emit('network-reconnected')
				}
			}
		)

		watch(() => currRoute.value, async () => {
			const qchatInitScript = document.querySelector('script[src="https://webchat.qontak.com/qchatInitialize.js"]');
			const qchatWidgetScript = document.querySelector('script[src="https://webchat.qontak.com/js/app.js"]');
			const qChatWidgetIframe = document.querySelector('iframe[id="qontak-webchat-widget"]');

			const hideIframe = () => {
				if (qChatWidgetIframe) {
					qChatWidgetIframe.style.display = 'none';
				}
			};
			const showIframe = () => {
				if (qChatWidgetIframe) {
					qChatWidgetIframe.style.display = 'block';
				}
			};

			if (!dashboard_restric.includes(currRoute.value)) {
				// Hanya panggil auth_check jika sesi belum diverifikasi di guard,
				// untuk menghindari request /api/auth/self redundan di tiap navigasi.
				if (!isSessionVerified()) {
					store.dispatch('Auth/auth_check')
						.then((resp) => {
							markSessionVerified()
							if (!qchatInitScript && !qchatWidgetScript) {
								if (resp.data.levels[0].name !== 'member') {
									const qchatInit = document.createElement('script');
									qchatInit.src = "https://webchat.qontak.com/qchatInitialize.js";

									const qchatWidget = document.createElement('script');
									qchatWidget.src = "https://webchat.qontak.com/js/app.js";

									document.head.prepend(qchatInit);
									document.head.prepend(qchatWidget);

									qchatInit.onload = function () {
										qchatInitialize({
											id: "1de27980-8df0-4ef8-a9a5-8ce14fb8294f",
											code: "5109QoM9UW5VNB-acX31Eg"
										});
									};
								}
							} else {
								showIframe();
							}
						})
						.catch(() => {
							// Offline: tetap di page. 401: ditangani interceptor.
						});
				} else {
					// Sesi sudah terverifikasi — cukup tampilkan qchat iframe jika perlu
					if (qChatWidgetIframe) showIframe();
				}
			} else {
				hideIframe();
			}
		})

		return {
			route,
			router,
			currRoute,
			dashboard_restric,
			isMemberRoute,
			isExtranetRoute,
			isMobile,
			showSplashVideo,
			refreshKey,
			toasts,
			removeToast,
		}
	},
};
</script>
